// Utility functions for color manipulation based on gradients

export type GradientType = 'sky-purple' | 'sunset' | 'ocean' | 'forest' | 'space';
export type IntensityType = 'light' | 'normal' | 'dark';

// Map gradients to complementary hue shifts (in degrees)
const gradientHueMap: Record<GradientType, number> = {
  'sky-purple': 60,    // Blue/Purple → Yellow/Orange (complementary)
  'sunset': -120,      // Orange/Red → Blue/Cyan (complementary)  
  'ocean': 180,        // Blue/Cyan → Orange/Red (complementary)
  'forest': -60,       // Green → Red/Pink (complementary)
  'space': 120,        // Purple/Indigo → Yellow/Green (complementary)
};

// Map intensity to saturation and brightness adjustments
const intensityMap: Record<IntensityType, { saturation: number; brightness: number }> = {
  light: { saturation: 0.3, brightness: 1.2 },
  normal: { saturation: 0.6, brightness: 1.0 },
  dark: { saturation: 0.9, brightness: 0.8 }
};

/**
 * Get the color from the gradient's end (bottom) for logo overlay
 * Uses the final color of the gradient for visual harmony
 */
export const getLogoOverlayColor = (gradient: string | number, intensity: string | number): string => {
  const gradientKey = gradient as GradientType;
  const intensityKey = intensity as IntensityType;

  // Extract the final color from each gradient (the "to-xxx" color)
  // Using Tailwind color values for precise matching
  const gradientEndColors: Record<GradientType, Record<IntensityType, string>> = {
    'sky-purple': {
      light: '#d8b4fe',   // purple-300
      normal: '#c084fc',  // purple-400
      dark: '#9333ea'     // purple-600
    },
    'sunset': {
      light: '#fca5a5',   // red-300
      normal: '#f87171',  // red-400
      dark: '#dc2626'     // red-600
    },
    'ocean': {
      light: '#5eead4',   // teal-300
      normal: '#2dd4bf',  // teal-400
      dark: '#0d9488'     // teal-600
    },
    'forest': {
      light: '#bef264',   // lime-300
      normal: '#a3e635',  // lime-400
      dark: '#65a30d'     // lime-600
    },
    'space': {
      light: '#000000',   // black
      normal: '#111827',  // gray-900
      dark: '#000000'     // black
    }
  };

  // Lower opacity for subtle, harmonious effect (not overwhelming)
  const opacity = 0.7;

  const color = gradientEndColors[gradientKey]?.[intensityKey] || gradientEndColors['sky-purple']['normal'];

  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get a complementary color for text shadows/effects
 */
export const getComplementaryDropShadow = (gradient: string | number, intensity: string | number): string => {
  const gradientKey = gradient as GradientType;
  const intensityKey = intensity as IntensityType;
  
  const baseColors: Record<GradientType, string> = {
    'sky-purple': intensityKey === 'dark' ? 'rgba(255,200,100,0.8)' : 'rgba(255,165,0,0.6)', // Orange glow
    'sunset': intensityKey === 'dark' ? 'rgba(100,200,255,0.8)' : 'rgba(0,150,255,0.6)',     // Blue glow
    'ocean': intensityKey === 'dark' ? 'rgba(255,100,100,0.8)' : 'rgba(255,100,50,0.6)',     // Red/Orange glow
    'forest': intensityKey === 'dark' ? 'rgba(255,100,150,0.8)' : 'rgba(255,50,100,0.6)',    // Pink glow
    'space': intensityKey === 'dark' ? 'rgba(200,255,100,0.8)' : 'rgba(150,255,50,0.6)'      // Yellow/Green glow
  };
  
  // Fallback to default if gradient not found
  const color = baseColors[gradientKey] || baseColors['sky-purple'];
  
  return `drop-shadow(2px 2px 8px ${color})`;
};